
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Subscription } from '@/lib/data';
import { SubscriptionFilters } from '@/components/subscriptions/SubscriptionFilters';
import { getSubscriptions, getSubscriptionById } from '@/services/subscription';
import { getInvoicesBySubscriptionId } from '@/services/invoiceService';
import { useQuery } from '@tanstack/react-query';
import { toast } from "sonner";

export function useSubscriptionList(customerId?: string) {
  const [filters, setFilters] = useState<SubscriptionFilters>({
    search: '',
    status: null,
    housingType: null,
    city: null,
  });
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fix: Add proper staleTime and retry configuration to prevent excessive requests
  const { 
    data: subscriptions = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['subscriptions', customerId],
    queryFn: () => getSubscriptions(customerId),
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error) => {
      // Only retry a limited number of times to prevent infinite loops
      return failureCount < 2;
    }
  });

  useEffect(() => {
    if (error) {
      toast.error(`Failed to load subscriptions: ${error instanceof Error ? error.message : "Unknown error"}`);
      console.error("Subscription loading error:", error);
    }
  }, [error]);

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter(subscription => {
      // If we're already filtering by customerId via the API, don't filter again
      if (customerId) return true;
      
      // Apply search filter
      if (filters.search && filters.search.trim() !== '') {
        const searchTerm = filters.search.toLowerCase().trim();
        
        const subscriptionNumber = subscription.subscription_number?.toLowerCase() || '';
        const customerName = subscription.customer?.customer_name?.toLowerCase() || '';
        const customerNumber = subscription.customer?.customer_number?.toLowerCase() || '';
        const installationStreet = subscription.installation_street?.toLowerCase() || '';
        const installationCity = subscription.installation_city?.toLowerCase() || '';
        const installationZipCode = subscription.installation_zip_code?.toLowerCase() || '';
        const alarmComId = subscription.alarm_com_id?.toLowerCase() || '';
        const customerPhone = subscription.customer?.phone?.toLowerCase() || '';
        const customerEmail = subscription.customer?.email?.toLowerCase() || '';
        const contactPhone = subscription.contact_phone?.toLowerCase() || '';
        const contactEmail = subscription.contact_email?.toLowerCase() || '';
        
        // Check subscription number, customer info, address fields, Alarm.com ID, phone numbers, and email addresses
        const matchesSearch = 
          subscriptionNumber.includes(searchTerm) ||
          customerName.includes(searchTerm) ||
          customerNumber.includes(searchTerm) ||
          installationStreet.includes(searchTerm) ||
          installationCity.includes(searchTerm) ||
          installationZipCode.includes(searchTerm) ||
          alarmComId.includes(searchTerm) ||
          customerPhone.includes(searchTerm) ||
          customerEmail.includes(searchTerm) ||
          contactPhone.includes(searchTerm) ||
          contactEmail.includes(searchTerm);
        
        if (!matchesSearch) return false;
      }

      // Apply status filter
      if (filters.status && filters.status !== 'all-statuses' && subscription.status !== filters.status) {
        return false;
      }

      // Apply housing type filter
      if (filters.housingType && filters.housingType !== 'all-types' && subscription.housing_type !== filters.housingType) {
        return false;
      }

      // Apply city filter
      if (filters.city && filters.city !== 'all-cities') {
        if (!subscription.installation_city || 
            subscription.installation_city.toLowerCase() !== filters.city.toLowerCase()) {
          return false;
        }
      }

      return true;
    });
  }, [subscriptions, filters, customerId]);
  
  // Calculate total pages based on filtered data
  const totalItems = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Get current page data
  const paginatedSubscriptions = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredSubscriptions.slice(startIndex, startIndex + pageSize);
  }, [filteredSubscriptions, page, pageSize]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  return {
    subscriptions: paginatedSubscriptions,
    filteredCount: filteredSubscriptions.length,
    isLoading,
    error,
    filters,
    setFilters,
    pagination: {
      page,
      setPage,
      pageSize,
      setPageSize,
      totalPages,
      totalItems
    }
  };
}

export function useSubscriptionDetail(subscriptionId: string) {
  // Fix: Add proper staleTime and retry configuration to prevent excessive requests
  const { 
    data: subscription, 
    isLoading: isLoadingSubscription, 
    error: subscriptionError 
  } = useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: () => getSubscriptionById(subscriptionId),
    enabled: !!subscriptionId,
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error) => {
      return failureCount < 2; // Only retry max 2 times
    }
  });

  const { 
    data: invoices = [], 
    isLoading: isLoadingInvoices, 
    error: invoicesError 
  } = useQuery({
    queryKey: ['subscription-invoices', subscriptionId],
    queryFn: () => getInvoicesBySubscriptionId(subscriptionId),
    enabled: !!subscriptionId,
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error) => {
      return failureCount < 2; // Only retry max 2 times
    }
  });

  useEffect(() => {
    if (subscriptionError) {
      toast.error(`Failed to load subscription details: ${subscriptionError instanceof Error ? subscriptionError.message : "Unknown error"}`);
      console.error("Subscription detail loading error:", subscriptionError);
    }
  }, [subscriptionError]);

  useEffect(() => {
    if (invoicesError) {
      toast.error(`Failed to load invoices: ${invoicesError instanceof Error ? invoicesError.message : "Unknown error"}`);
      console.error("Invoice loading error:", invoicesError);
    }
  }, [invoicesError]);

  const isLoading = isLoadingSubscription || isLoadingInvoices;
  const error = subscriptionError ? String(subscriptionError) : invoicesError ? String(invoicesError) : null;

  return {
    subscription,
    invoices,
    isLoading,
    error,
  };
}
